import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useHardDeleteCandidate } from '@/hooks/use-candidates';
import { usePermission } from '@/lib/permissions';
import { isConsentLocked } from '../lib/consent-actions';
import type { CandidateDetail } from '@/types/candidates';
import { useNavigate } from '@tanstack/react-router';
import {
  Check,
  Link2,
  Linkedin,
  Mail,
  MapPin,
  MoreHorizontal,
  Phone,
  Send,
  Trash2,
} from 'lucide-react';
import { type ReactNode, useMemo, useState } from 'react';
import { formatGdprLabel, GDPR_COLORS } from '../lib/candidate-styles';
import { CandidateStatusPill } from './candidate-status-pill';
import { HireCandidateDialog } from './hire-candidate-dialog';

function hashHue(input: string): number {
  let h = 0;
  for (let i = 0; i < input.length; i++) {
    h = (h * 31 + input.charCodeAt(i)) | 0;
  }
  return Math.abs(h) % 360;
}

function getInitials(name: string): string {
  const parts = name.trim().split(/\s+/);
  const first = parts[0]?.[0] ?? '';
  const last = parts.length > 1 ? parts[parts.length - 1][0] : '';
  return (first + last).toUpperCase() || '?';
}

interface CandidateProfileHeaderProps {
  candidate: CandidateDetail;
}

export function CandidateProfileHeader({ candidate }: CandidateProfileHeaderProps) {
  const hue = hashHue(candidate.fullName);
  const initials = getInitials(candidate.fullName);
  const navigate = useNavigate();
  const canDelete = usePermission('recruitment.candidates.delete');
  const hardDeleteMutation = useHardDeleteCandidate();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const [hireOpen, setHireOpen] = useState(false);
  const canHire = usePermission('recruitment.candidates.hire');
  const hasOpenApplication = candidate.assignments.some((a) => a.result === 'IN_PROGRESS');
  // Stable identity for the Hire dialog; only built while open so the dialog
  // stays dormant (no candidate fetch / defaults recompute) when closed.
  const hireCandidate = useMemo(
    () => ({
      id: candidate.id,
      firstName: candidate.firstName,
      lastName: candidate.lastName,
      email: candidate.email,
    }),
    [candidate.id, candidate.firstName, candidate.lastName, candidate.email],
  );

  const profileHref = `/recruitment/candidates/${candidate.id}`;
  // HW-1055: a candidate who revoked consent is not contacted — their email,
  // phone and Telegram stay readable but are no longer links to write to them.
  const contactLocked = isConsentLocked(candidate);

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(`${window.location.origin}${profileHref}`);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // clipboard unavailable — ignore
    }
  };

  return (
    <div className="flex items-start gap-5 rounded-lg border border-border bg-background p-6">
      <Avatar className="h-[86px] w-[86px] shrink-0 rounded-full">
        {candidate.photoUrl && (
          <AvatarImage src={candidate.photoUrl} alt={candidate.fullName} />
        )}
        <AvatarFallback
          className="rounded-full text-xl font-semibold text-foreground"
          style={{ background: `hsl(${hue}, 55%, 85%)` }}
        >
          {initials}
        </AvatarFallback>
      </Avatar>

      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-3">
          <div className="group flex min-w-0 items-center gap-1.5">
            <a
              href={profileHref}
              target="_blank"
              rel="noopener noreferrer"
              className="truncate text-2xl font-semibold tracking-tight text-foreground hover:underline"
              title={candidate.fullName}
            >
              {candidate.fullName}
            </a>
            <button
              type="button"
              onClick={handleCopyLink}
              aria-label="Copy profile link"
              title={copied ? 'Copied!' : 'Copy profile link'}
              className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md text-muted-foreground opacity-0 transition-opacity hover:bg-accent hover:text-foreground focus-visible:opacity-100 group-hover:opacity-100"
            >
              {copied ? (
                <Check className="h-3.5 w-3.5 text-emerald-500" />
              ) : (
                <Link2 className="h-3.5 w-3.5" />
              )}
            </button>
          </div>
          <CandidateStatusPill
            candidateId={candidate.id}
            status={candidate.status}
            onHire={canHire && hasOpenApplication ? () => setHireOpen(true) : undefined}
            hiredDisabledReason={
              canHire && !hasOpenApplication
                ? 'Assign the candidate to a vacancy first'
                : undefined
            }
            consentRevoked={isConsentLocked(candidate)}
          />
          <Badge
            variant="outline"
            className={`font-normal ${GDPR_COLORS[candidate.gdprStatus]}`}
          >
            GDPR: {formatGdprLabel(candidate.gdprStatus)}
          </Badge>
        </div>

        {candidate.headline && (
          <p className="mt-1 wrap-anywhere text-muted-foreground">{candidate.headline}</p>
        )}

        <div className="mt-3 flex flex-wrap gap-x-5 gap-y-1.5 text-sm">
          {candidate.email && (
            <ContactEntry
              href={`mailto:${candidate.email}`}
              locked={contactLocked}
              className="max-w-[260px]"
              title={candidate.email}
            >
              <Mail className="h-3.5 w-3.5 shrink-0" />
              <span className="truncate">{candidate.email}</span>
            </ContactEntry>
          )}
          {candidate.phone && (
            <ContactEntry
              href={`tel:${candidate.phone}`}
              locked={contactLocked}
              className="max-w-[260px]"
              title={candidate.phone}
            >
              <Phone className="h-3.5 w-3.5 shrink-0" />
              <span className="truncate">{candidate.phone}</span>
            </ContactEntry>
          )}
          {candidate.linkedinUrl && (
            <a
              href={candidate.linkedinUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 text-muted-foreground hover:text-foreground"
            >
              <Linkedin className="h-3.5 w-3.5" />
              LinkedIn
            </a>
          )}
          {candidate.telegram && (
            <ContactEntry
              href={`https://${candidate.telegram}`}
              locked={contactLocked}
              external
              title={candidate.telegram}
            >
              <Send className="h-3.5 w-3.5" />
              Telegram
            </ContactEntry>
          )}
          {candidate.location && (
            <span
              className="inline-flex max-w-[260px] items-center gap-1.5 text-muted-foreground"
              title={candidate.location}
            >
              <MapPin className="h-3.5 w-3.5 shrink-0" />
              <span className="truncate">{candidate.location}</span>
            </span>
          )}
        </div>
      </div>

      {canDelete && (
        <div className="shrink-0">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 text-muted-foreground"
                aria-label={`Actions for ${candidate.fullName}`}
              >
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem
                className="text-destructive focus:text-destructive"
                onClick={() => setConfirmOpen(true)}
              >
                <Trash2 className="mr-2 h-4 w-4" />
                Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle className="wrap-anywhere">
                  Permanently delete {candidate.fullName}?
                </AlertDialogTitle>
                <AlertDialogDescription>
                  This will permanently remove the candidate record together with all assignments,
                  notes, interviews and uploaded files. This action cannot be undone.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel disabled={hardDeleteMutation.isPending}>Cancel</AlertDialogCancel>
                <AlertDialogAction
                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                  disabled={hardDeleteMutation.isPending}
                  onClick={(e) => {
                    e.preventDefault();
                    hardDeleteMutation.mutate(candidate.id, {
                      onSuccess: () => {
                        setConfirmOpen(false);
                        navigate({ to: '/recruitment/candidates' });
                      },
                    });
                  }}
                >
                  {hardDeleteMutation.isPending ? 'Deleting…' : 'Delete'}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      )}

      <HireCandidateDialog
        open={hireOpen}
        candidate={hireOpen ? hireCandidate : null}
        onOpenChange={setHireOpen}
        onHired={() => setHireOpen(false)}
        onCancelled={() => {}}
      />
    </div>
  );
}

/**
 * One way to reach the candidate in the header. HW-1055: for a candidate who
 * revoked consent it is plain text — still readable, no longer a link to write
 * to them.
 */
function ContactEntry({
  href,
  locked,
  external = false,
  className,
  title,
  children,
}: {
  href: string;
  locked: boolean;
  external?: boolean;
  className?: string;
  title?: string;
  children: ReactNode;
}) {
  const base = `inline-flex items-center gap-1.5 text-muted-foreground ${className ?? ''}`;
  if (locked) {
    return (
      <span className={base} title={title}>
        {children}
      </span>
    );
  }
  return (
    <a
      href={href}
      className={`${base} hover:text-foreground`}
      title={title}
      {...(external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
    >
      {children}
    </a>
  );
}
